import React, { useEffect, useState } from 'react';
import { I18n } from 'react-redux-i18n';

import { I18N_BASE } from '../../constants';
import {
  BodyHashClusterItem,
  DisagreementReason,
  EventDetailResponse,
  FanOutResult,
  NOTES_MAX_LENGTH,
} from '../../models';
import { isDisagreementReasonRequired } from '../../helpers/isDisagreementReasonRequired';
import { getBlockConfirmLabel } from '../../helpers/getBlockConfirmLabelKey';
import { KEYBOARD_SHORTCUTS } from '../../constants';
import { useKeyboardShortcuts } from '../../hooks/useKeyboardShortcuts';
import CmButton from '../../components/primitives/CmButton';
import CmModal from '../../components/primitives/CmModal';
import BodyHashDuplicatesList from './BodyHashDuplicatesList';
import CollateralSummaryCard from './CollateralSummaryCard';
import DisagreementReasonRadioGroup from './DisagreementReasonRadioGroup';
import NotesField from './NotesField';
import styles from './modals.module.css';

export interface BlockSubmitPayload {
  selectedEventIds: number[];
  disagreementReason: DisagreementReason | null;
  notes: string | null;
}

export interface BlockConfirmationModalProps {
  open: boolean;
  detail: EventDetailResponse;
  pendingCount: number;
  bodyHashItems: BodyHashClusterItem[];
  selectedIds: number[];
  fanOutResults: Record<number, FanOutResult>;
  fanOutRunning: boolean;
  isSubmitting: boolean;
  onCancel: () => void;
  onToggleDuplicate: (id: number) => void;
  onConfirm: (payload: BlockSubmitPayload) => void;
  testId?: string;
}

function makeStats(detail: EventDetailResponse, pendingCount: number) {
  // pendingCount from the backend includes the current event itself; subtract
  // it to show "OTHER pending events on this account" — same convention as
  // AccountClusterPill on the review screen.
  const otherPending = Math.max(0, pendingCount - 1);
  return {
    otherPending,
    donations: detail.snapshot.raisedAmountSnapshot,
    donationCount: detail.snapshot.donationCountSnapshot,
    category: detail.event.aiCategory,
  };
}

export function BlockConfirmationModal({
  open,
  detail,
  pendingCount,
  bodyHashItems,
  selectedIds,
  fanOutResults,
  fanOutRunning,
  isSubmitting,
  onCancel,
  onToggleDuplicate,
  onConfirm,
  testId,
}: BlockConfirmationModalProps): React.ReactElement {
  const aiScore = detail.event.aiScore;
  const reasonRequired = isDisagreementReasonRequired(aiScore, 'block');
  // When reviewer is blocking AND the AI is leaning block too (score >= 7),
  // they're agreeing with the model — no disagreement reason needed, and the
  // section gets a greyed-skipped note instead of the radio group.
  const aiAgreesWithBlock = aiScore >= 7;
  const disagreementMode: 'agreed' | 'required' | 'optional' = aiAgreesWithBlock
    ? 'agreed'
    : reasonRequired
      ? 'required'
      : 'optional';
  const [reason, setReason] = useState<DisagreementReason | null>(null);
  const [notes, setNotes] = useState('');

  useEffect(() => {
    if (!open) {
      setReason(null);
      setNotes('');
    }
  }, [open]);

  // The triggering event is implicitly included alongside any duplicate selections.
  const sourceId = detail.event.contentModerationEventId;
  const effectiveSelected = Array.from(new Set([sourceId, ...selectedIds]));

  const notesTooLong = notes.length > NOTES_MAX_LENGTH;
  const reasonInvalid = reasonRequired && !reason;
  const submitDisabled = isSubmitting || fanOutRunning || notesTooLong || reasonInvalid;

  const confirmLabelMeta = getBlockConfirmLabel(effectiveSelected.length);

  const handleConfirm = () => {
    if (submitDisabled) return;
    onConfirm({
      selectedEventIds: effectiveSelected,
      disagreementReason: reason,
      notes: notes.trim() ? notes.trim() : null,
    });
  };

  // Enter submits the block while the modal is open and the form is valid.
  // The hook skips key events that originate inside textareas, so typing
  // notes still produces newlines.
  useKeyboardShortcuts(
    { [KEYBOARD_SHORTCUTS.confirm]: handleConfirm },
    { enabled: open },
  );

  const total = Object.values(fanOutResults).length;
  const succeeded = Object.values(fanOutResults).filter((r) => r.status === 'success').length;
  const failed = Object.values(fanOutResults).filter((r) => r.status === 'failure').length;
  const showAggregate = total > 0 && !fanOutRunning;

  return (
    <CmModal
      open={open}
      onClose={onCancel}
      title={I18n.t(`${I18N_BASE}.blockModal.title`)}
      testId={testId}
    >
      <p>{I18n.t(`${I18N_BASE}.blockModal.lead`)}</p>
      <CollateralSummaryCard
        stats={makeStats(detail, pendingCount)}
        testId="collateral-summary"
      />
      <BodyHashDuplicatesList
        items={bodyHashItems}
        selectedIds={selectedIds}
        onToggle={onToggleDuplicate}
        fanOutResults={fanOutResults}
        disabled={fanOutRunning}
        testId="duplicates-list"
      />
      <DisagreementReasonRadioGroup
        value={reason}
        onChange={setReason}
        mode={disagreementMode}
        decision="block"
        testId="disagreement"
      />
      <NotesField
        value={notes}
        onChange={setNotes}
        testId="block-notes"
      />
      {showAggregate ? (
        <div className={styles.aggregateBanner} data-testid="fan-out-aggregate">
          {I18n.t(`${I18N_BASE}.blockModal.fanOut.summary`, {
            succeeded,
            total,
            failed,
          })}
        </div>
      ) : null}
      <div className={styles.buttonRow}>
        <CmButton variant="ghost" onClick={onCancel} testId="block-cancel">
          {I18n.t(`${I18N_BASE}.blockModal.cancel`)}
        </CmButton>
        <CmButton
          variant="danger"
          onClick={handleConfirm}
          disabled={submitDisabled}
          loading={isSubmitting || fanOutRunning}
          testId="block-confirm"
        >
          {I18n.t(confirmLabelMeta.i18nKey, confirmLabelMeta.params)}
        </CmButton>
      </div>
    </CmModal>
  );
}

export default BlockConfirmationModal;
